<template lang="pug">
div
  //- Loading State
  template(v-if="isFetching")
    yz-card.mb-4(v-for="n in 3" :key="n" outlined)
      .pa-4
        yz-skeleton-loader(type="list-item-avatar-two-line")

  //- Error State
  template(v-else-if="isError")
    yz-empty-state-card(icon="$yzi-alert-circle" icon-color="yz-danger-500" title="Unable to load warehouses" description="Please try again" action-label="Retry" @action-click="fetchWarehouses")

  //- Empty State (no warehouses at all) - HQ mode shows add button
  template(v-else-if="warehouses.length === 0 && !hasActiveFilters && isHqMode")
    yz-empty-state-card(icon="$yzi-building-01" title="No warehouse added" description="Add warehouse to see information here" action-label="Add warehouse" @action-click="onAddWarehouse")
      template(v-slot:action)
        yz-btn(label="Add warehouse" prepend-icon="$yzi-plus" size="sm" @click="onAddWarehouse")

  //- Empty State (no warehouses at all) - Branch mode view only
  template(v-else-if="warehouses.length === 0 && !hasActiveFilters && !isHqMode")
    yz-card.pa-8(outlined)
      .text-center.neutral500--text
        v-icon.mb-4(size="48" color="neutral400") $yzi-building-01
        .text-sm No warehouses found

  //- Empty State (filtered results empty)
  template(v-else-if="warehouses.length === 0")
    yz-card.pa-8(outlined)
      .text-center.text-neutral-500
        v-icon.mb-4(size="48" color="neutral400") $yzi-search-lg
        .text-sm No warehouses match your filters

  //- Content
  template(v-else)
    yz-card.mb-4.cursor-pointer.hover-bg(v-for="warehouse in warehouses" :key="warehouse.id" outlined @click="onWarehouseClick(warehouse)")
      .d-flex.align-center.pa-4
        //- Building icon
        .mr-4
          yz-icon(size="30" color="neutral-500") $yzi-building-01

        //- Content section
        .flex-grow-1
          .font-semibold.text-sm.text-neutral-700.mb-1 {{ warehouse.name }}
          .d-flex.align-center.text-sm.text-neutral-500.flex-wrap
            template(v-if="warehouse.formattedAddress")
              span {{ warehouse.formattedAddress }}
            template(v-if="warehouse.email")
              yz-divider.mx-2(v-if="warehouse.formattedAddress" vertical)
              span {{ warehouse.email }}
            template(v-if="warehouse.phone")
              yz-divider.mx-2(v-if="warehouse.email" vertical)
              yz-btn(underline, :label="'+' + warehouse.phone" append-icon="mdi-whatsapp" icon-color="neutral-500" @click.stop="onOpenWhatsApp(warehouse.phone)")

        //- Actions - Only show menu for HQ users
        .d-flex.align-center
          template(v-if="isHqMode")
            yz-menu(offset-y left)
              template(v-slot:activator="{ on, attrs }")
                yz-btn.mr-2(icon="$yzi-dots-horizontal" icon-only v-bind="attrs" v-on="on" @click.stop)
              yz-list
                yz-list-item(@click="onEdit(warehouse)")
                  div Edit
                yz-divider
                yz-list-item(@click="onAddStorageLocation(warehouse)")
                  div Add storage location
                yz-divider
                yz-list-item(@click="onDelete(warehouse)")
                  .text-danger-500 Delete

          yz-btn(:icon="isExpanded(warehouse.id) ? '$yzi-chevron-up' : '$yzi-chevron-down'" icon-only @click.stop="onToggleExpand(warehouse.id)")

      //- Expanded content - Storage locations table
      template(v-if="isExpanded(warehouse.id)")
        .px-4.pb-4.table-scroll-wrapper
          yz-data-table(:headers="storageHeaders" :items="warehouse.storageLocations" header-class="yz-medium-body-xs" body-class="yz-regular-body-sm")
            template(v-slot:item.name="{ item }")
              span.font-medium {{ item.name }}
            template(v-slot:item.createdDate="{ item }")
              span {{ item.createdDate }}
            template(v-slot:item.inventories="{ item }")
              span {{ item.inventories }}

  //- Add/Edit Warehouse Dialog (HQ mode: editable, Branch mode: readonly view)
  add-warehouse-dialog(v-if="showAddWarehouseDialog" :open.sync="showAddWarehouseDialog" :warehouse="selectedWarehouse" :readonly="!isHqMode" @saved="onWarehouseSaved" @storage-changed="onWarehouseSaved")

  //- Add Storage Location Dialog (HQ only)
  add-storage-location-dialog(v-if="showAddStorageDialog && isHqMode" :open.sync="showAddStorageDialog" :selected-branch="selectedWarehouseForStorage" @saved="onStorageLocationSaved" @deleted="onStorageLocationDeleted")

  //- Delete Warehouse Confirmation Dialog (HQ only)
  yz-delete-confirmation-dialog(v-if="isHqMode" v-model="showDeleteConfirmDialog" item="warehouse" description="This action will permanently delete the warehouse and all its storage locations. This cannot be undone." :loading="isDeleting" @confirmed="confirmDeleteWarehouse" @canceled="cancelDeleteWarehouse")
    //- Warehouse details card
    yz-card.mt-4.pa-4(v-if="warehouseToDelete" outlined)
      .yz-medium-body-sm.text-neutral-700 {{ warehouseToDelete.name }}
      .yz-regular-body-sm.text-neutral-500.mt-1(v-if="warehouseToDelete.formattedAddress") {{ warehouseToDelete.formattedAddress }}
      .d-flex.align-center.mt-2.yz-regular-body-sm.text-neutral-500(v-if="warehouseToDelete.email || warehouseToDelete.phone")
        span(v-if="warehouseToDelete.email") {{ warehouseToDelete.email }}
        yz-divider.mx-2(v-if="warehouseToDelete.email && warehouseToDelete.phone" vertical)
        span(v-if="warehouseToDelete.phone") +{{ warehouseToDelete.phone }}
      .yz-regular-body-xs.text-neutral-500.mt-2(v-if="warehouseToDelete.storageLocations && warehouseToDelete.storageLocations.length > 0") {{ warehouseToDelete.storageLocations.length }} storage location(s) will also be deleted
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddWarehouseDialog from './dialog/AddWarehouseDialog.vue'
import AddStorageLocationDialog from './dialog/AddStorageLocationDialog.vue'

export default {
  components: {
    AddWarehouseDialog,
    AddStorageLocationDialog
  },
  props: {
    filters: {
      type: Array,
      default: () => []
    },
    searchText: {
      type: String,
      default: ''
    },
    isHqMode: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      expandedWarehouses: [],
      isFetching: true,
      isError: false,
      warehouses: [],
      totalWarehouses: 0,
      showAddWarehouseDialog: false,
      selectedWarehouse: null,
      showAddStorageDialog: false,
      selectedWarehouseForStorage: null,
      showDeleteConfirmDialog: false,
      warehouseToDelete: null,
      isDeleting: false,
      storageHeaders: [
        { text: 'Storage location', value: 'name', sortable: false },
        { text: 'Created date', value: 'createdDate', sortable: false },
        { text: 'Inventories', value: 'inventories', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    companyIdForListing() {
      // Use HQ company ID if this is a branch, otherwise use own company ID
      return this.company?.branch_of?.id || this.company?.id
    },
    hasActiveFilters() {
      return this.filters.length > 0 || this.searchText
    }
  },
  watch: {
    filters: {
      handler() {
        this.fetchWarehouses()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchWarehouses()
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchWarehouses() {
      this.isFetching = true
      this.isError = false

      const params = {}

      // Add os param to filter warehouses only
      params.os = 'warehouse'

      // Add search param
      if (this.searchText) {
        params.search = this.searchText
      }

      // Add filter params from filters array
      this.filters.forEach(filter => {
        if (filter.key === 'date' && filter.value) {
          // Handle date filter
          const dates = Array.isArray(filter.value) ? filter.value : [filter.value, filter.value]
          params.created__gte = dates[0]
          params.created__lte = dates[1]
        } else if (filter.key && filter.value) {
          // Handle other filters (name, etc.)
          params[filter.key] = filter.value
        }
      })

      try {
        const response = await this.$axios.get(`/companies/${this.companyIdForListing}/branches`, { params })
        this.warehouses = response.data.results.map(warehouse => ({
          id: warehouse.id,
          slug: warehouse.slug,
          name: warehouse.name,
          email: warehouse.email || null,
          phone: warehouse.phone || null,
          address: warehouse.address,
          formattedAddress: this.formatAddress(warehouse.address),
          createdDate: warehouse.created ? new Date(warehouse.created) : null,
          storageLocations: (warehouse.storages || []).map(storage => ({
            id: storage.id,
            name: storage.value,
            value: storage.value,
            createdDate: storage.created ? this.formatDate(storage.created) : '-',
            inventories: storage.products?.length || 0,
            products: storage.products || []
          }))
        }))
        this.totalWarehouses = response.data.count
      } catch (error) {
        console.error('Error fetching warehouses:', error)
        this.isError = true
      } finally {
        this.isFetching = false
      }
    },
    formatAddress(address) {
      if (!address) return null
      const parts = [address.address_1, address.address_2, address.postcode, address.city, address.state].filter(Boolean)
      return parts.join(', ')
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    onAddWarehouse() {
      if (!this.isHqMode) return
      this.selectedWarehouse = null
      this.showAddWarehouseDialog = true
    },
    openAddWarehouseDialog() {
      this.onAddWarehouse()
    },
    onOpenWhatsApp(phone) {
      const cleanPhone = phone.replace(/[^0-9]/g, '')
      window.open(`https://api.whatsapp.com/send?phone=${cleanPhone}&app_absent=1`)
    },
    onWarehouseClick(warehouse) {
      // HQ can edit, branches view in readonly mode
      if (this.isHqMode) {
        this.onEdit(warehouse)
      } else {
        this.onViewDetails(warehouse)
      }
    },
    onViewDetails(warehouse) {
      this.selectedWarehouse = warehouse
      this.showAddWarehouseDialog = true
    },
    onEdit(warehouse) {
      if (!this.isHqMode) return
      this.selectedWarehouse = warehouse
      this.showAddWarehouseDialog = true
    },
    onWarehouseSaved() {
      this.fetchWarehouses()
    },
    onAddStorageLocation(warehouse) {
      if (!this.isHqMode) return
      this.selectedWarehouseForStorage = warehouse
      this.showAddStorageDialog = true
    },
    onStorageLocationSaved() {
      this.fetchWarehouses()
    },
    onStorageLocationDeleted() {
      this.fetchWarehouses()
    },
    onDelete(warehouse) {
      if (!this.isHqMode) return
      this.warehouseToDelete = warehouse
      this.showDeleteConfirmDialog = true
    },
    async confirmDeleteWarehouse() {
      if (!this.warehouseToDelete || !this.isHqMode) return

      this.isDeleting = true
      try {
        await this.$axios.delete(`/companies/${this.company.id}/branches/${this.warehouseToDelete.id}`)
        this.showMessage(['success', 'Warehouse deleted successfully'])
        this.showDeleteConfirmDialog = false
        this.warehouseToDelete = null
        await this.fetchWarehouses()
      } catch (error) {
        console.error('Error deleting warehouse:', error)
        const errorMessage = error.response?.data?.detail || 'Failed to delete warehouse'
        this.showMessage(['error', errorMessage])
      } finally {
        this.isDeleting = false
      }
    },
    cancelDeleteWarehouse() {
      this.showDeleteConfirmDialog = false
      this.warehouseToDelete = null
    },
    onToggleExpand(warehouseId) {
      const index = this.expandedWarehouses.indexOf(warehouseId)
      if (index > -1) {
        this.expandedWarehouses.splice(index, 1)
      } else {
        this.expandedWarehouses.push(warehouseId)
      }
    },
    isExpanded(warehouseId) {
      return this.expandedWarehouses.includes(warehouseId)
    }
  }
}
</script>

<style lang="scss" scoped>
.table-scroll-wrapper {
  overflow-x: auto;
}
.table-scroll-wrapper ::v-deep .yz-data-table {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper > table {
  min-width: 1400px;
}
</style>
